import { FormEvent, useEffect, useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { confirmPhoneChange, requestPhoneChange } from '@api/auth/apiService';
import { formatUsPhone } from '@features/settings-personal-info/utils/phone';

// Same wait the backend enforces between two codes.
const RESEND_WAIT_SECONDS = 60;

type Step = 'number' | 'code';

interface ChangePhoneDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: (phone: string) => void;
}

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const isValidPhone = (value: string) =>
  parsePhoneNumberFromString(value, 'US')?.isValid() || false;

/**
 * Change the account phone number (ZLAYT-1069). A code goes to the NEW number
 * and the number on the account changes only once that code is confirmed.
 */
function ChangePhoneDialog({
  open,
  onClose,
  onChanged,
}: ChangePhoneDialogProps) {
  const [step, setStep] = useState<Step>('number');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sentMessage, setSentMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!open) {
      setStep('number');
      setPhone('');
      setCode('');
      setSentMessage('');
      setError('');
      setBusy(false);
      setSecondsLeft(0);
    }
  }, [open]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  const sendCode = async () => {
    if (!isValidPhone(phone)) {
      setError('Enter a valid phone number');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await requestPhoneChange(phone);
      setSentMessage(response?.message || 'Code sent to your phone');
      setCode('');
      setStep('code');
      setSecondsLeft(RESEND_WAIT_SECONDS);
    } catch (err) {
      setError(
        errorMessage(err, 'We could not send a code. Please try again.')
      );
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) {
      setError('Enter the code we sent you');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await confirmPhoneChange(code.trim());
      onChanged(response?.phone || phone);
    } catch (err) {
      setError(errorMessage(err, 'We could not verify that code.'));
      setBusy(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    if (step === 'number') {
      sendCode();
    } else {
      verify();
    }
  };

  const useDifferentNumber = () => {
    setStep('number');
    setCode('');
    setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <form onSubmit={onSubmit} noValidate>
        <DialogTitle>
          {step === 'number' ? 'Change phone number' : 'Verify this number'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} pt={1}>
            {step === 'number' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  We will text a code to the new number. Your number changes
                  only after you enter that code.
                </Typography>
                <TextField
                  id="change-phone-new-number"
                  label="New phone number"
                  placeholder="+1 (512) 555-0199"
                  value={phone}
                  onChange={(e) => setPhone(formatUsPhone(e.target.value))}
                  inputProps={{ inputMode: 'tel' }}
                  autoComplete="tel"
                  autoFocus
                  fullWidth
                />
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  {sentMessage}
                </Typography>
                <TextField
                  id="change-phone-code"
                  label="Verification code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 8 }}
                  autoComplete="one-time-code"
                  autoFocus
                  fullWidth
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={sendCode}
                    disabled={busy || secondsLeft > 0}
                  >
                    {secondsLeft > 0
                      ? `Resend code in ${secondsLeft}s`
                      : 'Resend code'}
                  </Button>
                  <Button
                    size="small"
                    onClick={useDifferentNumber}
                    disabled={busy}
                  >
                    Use a different number
                  </Button>
                </Stack>
              </>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {step === 'number' ? 'Send code' : 'Verify'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ChangePhoneDialog;
